import type { Image } from "@workspace/crystallize/generated/discovery/";
import { cn } from "@workspace/ui/lib/utils";
import React from "react";
import {
  type ColumnsConfig,
  DEFAULT_BREAKPOINTS,
  sizesFromColumns,
} from "~/utils/helpers/sizes-from-columns";

interface CrystallizeResponsiveImageProps {
  image: Image | null | undefined;
  alt?: string;
  className?: string;
  fetchPriority?: "high" | "low" | "auto";
  sizes?: string;
  columns?: ColumnsConfig;
  clampPx?: number;
  widths?: number[];
}

const DEFAULT_WIDTHS = [320, 480, 640, 768, 1024, 1280, 1536, 1920];

function imageProxyUrl(key: string, width: number) {
  return `/images/${key}?width=${width}`;
}

function buildSrcSet(key: string, widths: number[]) {
  return widths.map((w) => `${imageProxyUrl(key, w)} ${w}w`).join(", ");
}

const CrystallizeResponsiveImage: React.FC<CrystallizeResponsiveImageProps> = ({
  image,
  alt,
  className,
  fetchPriority = "low",
  sizes,
  columns = { sm: 1, md: 2, lg: 3 },
  clampPx,
  widths = DEFAULT_WIDTHS,
}) => {
  if (!image) return null;

  const key = image.key;
  if (!key) return null;

  const computedSizes =
    sizes ?? sizesFromColumns(columns, DEFAULT_BREAKPOINTS, clampPx);

  return (
    <div className="relative h-full w-full bg-accent">
      <img
        src={imageProxyUrl(key, 800)}
        srcSet={buildSrcSet(key, widths)}
        sizes={computedSizes}
        alt={alt ?? image.altText ?? ""}
        width={image.width ?? undefined}
        height={image.height ?? undefined}
        className={cn(className)}
        fetchPriority={fetchPriority}
        loading={fetchPriority === "high" ? "eager" : "lazy"}
      />
    </div>
  );
};

export default CrystallizeResponsiveImage;
